import { capFirst, gtag } from "#js/components/utils"
import { message } from "#js/components/snackbar"
import { css, html } from "lit"
import { msg, str, updateWhenLocaleChanges } from "@lit/localize"
import { TrackingMixin } from "#js/components/TrackingMixin"
import { getUrlWithTracking } from "#js/components/utm"
import { picture } from "#js/components/ui"
import { sendAlgoliaClickEvent } from "#js/integrations/algoliaSearch"
import { setLocale } from "#js/components/lit-i18n"
import { unsafeHTML } from "lit/directives/unsafe-html.js"
import { addToWishList, removeFromWishList } from "#js/components/tracking"

export class OfferCard extends TrackingMixin {
  static get properties() {
    return {
      offer: { type: Object },
    }
  }

  constructor() {
    super()
    setLocale(globalThis.language)
    updateWhenLocaleChanges(this)
  }

  static styles = css`
    .job-title {
      font-size: 80%;
      margin: 0 0 0.5em;
      color: var(--grey);
    }
  `

  shouldShowHostPicture() {
    return this.offer.host_picture && this.offer.activity_type !== "counseling"
  }

  csrfToken = document.querySelector("input[name=csrfmiddlewaretoken]").value

  render() {
    if (!this.offer) {
      console.warn("<offer-card> component rendered without properties assigned.")
      // Component was loaded into the dom, but has no properties assigned.
      // This happens when it is contained in another component or renderer.
      return
    }
    return this.tracking(html`
      <link rel="stylesheet" href="${globalThis.styleFilePath}" />
      <a
        href="${getUrlWithTracking(this.offer.public_url, this.trackingInformation)}"
        @click="${(event) => this.sendSelectItemOfferCard(event, this.offer)}"
        class="card ${this.getCardType()}"
      >
        <div class="card__layer card__layer--offer">
          ${this.getPicture()} ${this.getHostPicture()}
          <div class="ribbon__wrapper">${this.getRibbon()}</div>
          ${this.getWatchlist()} ${this.getDebugInfo()}
        </div>
        <div class="card__content card__content--offer">
          ${this.getTimer()}
          <section class="column column--spaced" style="height: 100%; gap: 1em;">
            <div>${this.getTitle()} ${this.getSubtitle()}</div>
            ${this.getBubbles()}
          </section>
        </div>
      </a>
    `)
  }

  tracking(body) {
    return html`${body}`
  }

  getCardType() {
    if (this.offer.type === "expert") {
      return "card--expert"
    } else {
      return "card--offer"
    }
  }

  getTitle() {
    if (this.offer.type === "expert") {
      return html`
        <h4 style="margin-block-end: 0">${this.offer.title}</h4>
        <p class="hyphenate job-title">${this.offer.job_title}</p>
      `
    } else {
      return html` <h4>${this.offer.title}</h4> `
    }
  }

  getSubtitle() {
    if (this.offer.type === "expert") {
      return html` <p class="hyphenate">${this.offer.areas_of_expertise}</p> `
    } else {
      return html`
        <p class="hyphenate ellipsis" style="max-height: 6em; overflow: hidden">
          ${this.offer.subtitle}
        </p>
      `
    }
  }

  squarePictureTypes = ["expert", "host"]

  getPicture() {
    if (this.offer.picture) {
      return html`
        ${unsafeHTML(
          picture(
            this.offer.picture,
            this.offer.title,
            this.squarePictureTypes.includes(this.offer.type) ? "1/1" : "3/2",
            {
              style: `width: 100%; filter: ${
                this.shouldShowHostPicture() ? "opacity(0.3)" : "none"
              }`,
            },
            { class: "card__image card__image--offer", loading: "lazy" },
          ),
        )}
      `
    }
  }

  getHostPicture() {
    if (this.shouldShowHostPicture()) {
      return html`
        ${unsafeHTML(
          picture(
            this.offer.host_picture,
            this.offer.provider_title,
            "1/1",
            { class: "host__image-wrapper" },
            {
              class: "host__image",
              loading: "lazy",
            },
          ),
        )}
      `
    }
  }

  getRibbon() {
    if (this.offer.activity_type_display) {
      return html` <div class="ribbon">${this.offer.activity_type_display}</div> `
    }
  }

  getBubbles() {
    return html`
      <div class="bubble__wrapper bubble__wrapper--offer">
        ${[...this.getLanguageTags()]} ${[...this.getProfessionalLanguageTags()]}
        ${this.getLocationBubble()} ${this.getAgeBubble()}
      </div>
    `
  }

  getWatchlist() {
    const watchURLs = this.offer.watch_url && this.offer.unwatch_url
    if (watchURLs) {
      if (this.offer.watched) {
        return html`
          <div class="watchlist__wrapper">
            <div class="watchlist" @click="${this.unwatch}">
              <svg viewBox="0 0 125 108" class="watchlist__heart">
                <use
                  href="${globalThis.svgSprite}#heartFilled"
                  style="color: var(--rose);"
                ></use>
              </svg>
            </div>
          </div>
        `
      } else {
        return html`
          <div class="watchlist__wrapper">
            <div class="watchlist" @click="${this.watch}">
              <svg viewBox="0 0 125 108" class="watchlist__heart">
                <use href="${globalThis.svgSprite}#heart"></use>
              </svg>
            </div>
          </div>
        `
      }
    }
  }

  watch(event) {
    event.preventDefault()
    event.stopImmediatePropagation()
    this.offer.watched = true
    fetch(this.offer.watch_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
    })
    message(
      msg(str`Added '${this.offer.title}' to watchlist.`),
      "success",
      capFirst(msg(str`view watchlist`)),
      this.goToWatchlist,
    )
    addToWishList({
      value: this.offer.price,
      item_name: this.offer.title,
      item_id: this.offer.id,
      item_brand: this.offer.provider_title,
      affiliation: this.offer.brand_name,
      price: this.offer.price,
    })
    this.requestUpdate()
  }

  unwatch(event) {
    event.preventDefault()
    event.stopImmediatePropagation()
    this.offer.watched = false
    fetch(this.offer.unwatch_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": this.csrfToken,
      },
    })
    message(
      msg(str`Removed '${this.offer.title}' from watchlist.`),
      "warning",
      capFirst(msg(str`view watchlist`)),
      this.goToWatchlist,
    )
    removeFromWishList({
      value: this.offer.price,
      item_name: this.offer.title,
      item_id: this.offer.id,
      item_brand: this.offer.provider_title,
      affiliation: this.offer.brand_name,
      price: this.offer.price,
    })
    this.requestUpdate()
  }

  /** Redirects the user to the watchlist page. */
  goToWatchlist() {
    globalThis.location.href = globalThis.siteUrls.watchlist
  }

  getDebugInfo() {
    if (globalThis.debug) {
      return html`<small class="card__sort_order">${this.offer.sort_order}</small>`
    }
  }

  getTimer() {
    if (this.offer.booking_option_timer) {
      return html`
        <div class="card__timer card__timer--offer-card">
          <svg class="icon">
            <use href="${globalThis.svgSprite}#calendar"></use>
          </svg>
          <span> &nbsp;${capFirst(this.offer.booking_option_timer)} </span>
        </div>
      `
    }
  }

  shouldShowLanguageTags() {
    return (
      this.offer.activity_type === "counseling" ||
      (this.offer.activity_type === "talk" && !this.offer.booking_option_timer) ||
      this.offer.activity_type === "recording"
    )
  }

  *getLanguageTags() {
    if (this.offer.languages && this.shouldShowLanguageTags()) {
      for (const code of Object.keys(this.offer.languages).sort((a, b) =>
        b === globalThis.language ? 1 : -1,
      )) {
        yield html` <div class="bubble bubble--small">${code.toUpperCase()}</div> `
      }
    }
  }

  *getProfessionalLanguageTags() {
    if (this.offer.secondary_languages && this.shouldShowLanguageTags()) {
      for (const code of Object.keys(this.offer.secondary_languages).sort((a, b) =>
        b === globalThis.language ? 1 : -1,
      )) {
        yield html`
          <div class="bubble bubble--small">
            ${code.toUpperCase()}
            ${this.offer.activity_type !== "counseling"
              ? html`
                  <span style="line-height: 1.5">&nbsp;&#x2022;&nbsp;</span>
                  <tabler-icon
                    name="badge-cc"
                    style="display: block; line-height: 0.5; margin-top: 0.15em;"
                  ></tabler-icon>
                `
              : ""}
          </div>
        `
      }
    }
  }

  getLocationBubble() {
    if (this.offer.location && this.offer.location !== "online") {
      return html` <div class="bubble bubble--small">${this.offer.location}</div> `
    }
  }

  getAgeBubble() {
    if (this.offer.age_range) {
      return html` <div class="bubble bubble--small">${this.offer.age_range}</div> `
    }
  }

  sendSelectItemOfferCard(event, item) {
    gtag("event", "select_item", {
      items: [
        {
          item_name: item.title,
          item_id: item.id,
          item_brand: item.provider_title,
          affiliation: item.brand_name,
          quantity: 1,
          item_list_id: this.itemListId,
          item_list_name: this.itemListName,
        },
      ],
    })

    sendAlgoliaClickEvent(item.algoliaAnalytics)
  }
}

globalThis.customElements.define("offer-card", OfferCard)
